import React, { useMemo, useRef, useEffect } from 'react';
import { parseWeekSeries, getWeekDateRange } from '../utils/timeUtils';
import { getColorForCourse, brightenHexColor } from '../utils/colorUtils';
import { timeToMinutes, getWeekNumber, isEntryRoomDeleted } from '../utils/helpers.ts';
import { SCHEDULE_START_MINUTE, SCHEDULE_END_MINUTE } from '../config/constants';
import { useActiveDays } from '../hooks/useActiveDays';

const TeacherView = ({
    teacher,
    scheduledInstances,
    startWeek = 36,
    year,
    user,
    rooms,
    onOpenModal,
    timelineViewRange
}) => {
    // Get active days from AssetManager
    const { activeDaysCapitalized: daysOfWeek, loading: daysLoading } = useActiveDays();
    const currentWeekRefs = useRef({});
    const currentWeek = getWeekNumber(new Date());
    const currentYear = new Date().getFullYear();

    // Generate weeks from startWeek to end of year, then from week 1 to startWeek-1 of next year
    const weeks = useMemo(() => {
        const currentYear = year || new Date().getFullYear();
        const weeksInYear = 53; // Assuming 53 weeks max
        const weeksList = [];

        // Add weeks from startWeek to end of current year
        for (let week = startWeek; week <= weeksInYear; week++) {
            weeksList.push({ year: currentYear, week });
        }

        // Add weeks from start of next year to startWeek-1
        // This handles academic years that span calendar years
        for (let week = 1; week < startWeek; week++) {
            weeksList.push({ year: currentYear + 1, week });
        }

        // Filter weeks based on timeline view range if provided
        if (timelineViewRange) {
            return weeksList.filter(weekInfo =>
                timelineViewRange.isWeekInRange(weekInfo.week, weekInfo.year)
            );
        }

        return weeksList;
    }, [startWeek, year, timelineViewRange]);

    // Filter scheduled instances for the selected teacher (supports multi-teacher associations)
    const teacherScheduleInstances = useMemo(() => {
        const teacherName = (typeof teacher === 'object' && teacher !== null) ? teacher.name : (teacher || '');
        if (!teacherName) return [];
        return scheduledInstances.filter(instance => typeof instance?.hasTeacher === 'function' ? instance.hasTeacher(teacherName) : (instance.teacher === teacherName));
    }, [scheduledInstances, teacher]);

    // Group instances by week and day
    const scheduleByWeekAndDay = useMemo(() => {
        const schedule = {};

        teacherScheduleInstances.forEach((instance) => {
            if (!instance.weekSeries) {
                return;
            }

            const weekNumbers = parseWeekSeries(instance.weekSeries);

            weekNumbers.forEach(weekNum => {
                const weekKey = `${year}-${weekNum}`;

                // Ensure day is stored consistently (use lowercase)
                const dayKey = instance.day.toLowerCase();

                if (!schedule[weekKey]) {
                    schedule[weekKey] = {};
                }
                if (!schedule[weekKey][dayKey]) {
                    schedule[weekKey][dayKey] = [];
                }
                schedule[weekKey][dayKey].push(instance);
            });
        });

        return schedule;
    }, [teacherScheduleInstances, year]);

    // Calculate time grid parameters
    const timeRange = SCHEDULE_END_MINUTE - SCHEDULE_START_MINUTE;
    const pixelsPerMinute = 2; // Adjust this to change the scale
    const totalGridWidth = timeRange * pixelsPerMinute;

    // Generate hour markers
    const hourMarkers = useMemo(() => {
        const markers = [];
        const startHour = Math.floor(SCHEDULE_START_MINUTE / 60);
        const endHour = Math.ceil(SCHEDULE_END_MINUTE / 60);

        for (let hour = startHour; hour <= endHour; hour++) {
            const hourMinutes = hour * 60;
            if (hourMinutes >= SCHEDULE_START_MINUTE && hourMinutes <= SCHEDULE_END_MINUTE) {
                const position = (hourMinutes - SCHEDULE_START_MINUTE) * pixelsPerMinute;
                markers.push({
                    hour,
                    position,
                    label: `${hour}:00`
                });
            }
        }

        return markers;
    }, [pixelsPerMinute]);

    // Function to scroll to current week
    const scrollToCurrentWeek = () => {
        const currentWeekKey = `${currentYear}-${currentWeek}`;
        const currentWeekElement = currentWeekRefs.current[currentWeekKey];
        if (currentWeekElement) {
            currentWeekElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const renderCourseBlock = (instance) => {
        const startMinutes = timeToMinutes(instance.timeStart);
        const endMinutes = timeToMinutes(instance.timeEnd);

        if (startMinutes === null || endMinutes === null) {
            return null;
        }

        const startPosition = (startMinutes - SCHEDULE_START_MINUTE) * pixelsPerMinute;
        const duration = endMinutes - startMinutes;
        const width = duration * pixelsPerMinute;

        if (width <= 0) {
            return null;
        }

        let adjustedStartPosition = Math.max(0, startPosition);
        let adjustedWidth = startPosition < 0 ? width + startPosition : width;

        if (adjustedWidth <= 0) {
            return null;
        }

        // Prefer subject-provided hex color when available; fallback to class-based palette
        const subjectHexColor = instance?.subjectObj?.color;
        const colorClass = subjectHexColor ? '' : getColorForCourse(instance.course);
        const roomDeleted = isEntryRoomDeleted(instance, rooms);

        return (
            <div
                key={`${instance.id}-${instance.day}`}
                className={`absolute h-14 ${colorClass} rounded-md border cursor-pointer overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200 ${roomDeleted ? 'border-rose-400' : 'border-gray-300'}`}
                style={{
                    left: `${adjustedStartPosition}px`,
                    width: `${adjustedWidth}px`,
                    zIndex: 1,
                    ...(subjectHexColor ? { backgroundColor: brightenHexColor(subjectHexColor), borderColor: roomDeleted ? undefined : subjectHexColor } : {})
                }}
                onClick={() => onOpenModal && onOpenModal(instance)}
                title={`${instance.course}\n${instance.group}\n${instance.room}${roomDeleted ? ' (Room Deleted)' : ''}\n${instance.timeStart} - ${instance.timeEnd}`}
            >
                {roomDeleted && (
                    <div className="bg-rose-600 text-white text-[9px] font-bold uppercase tracking-wide text-center leading-none py-0.5">
                        Room Deleted
                    </div>
                )}
                <div className={`px-1 text-xs text-gray-600 font-medium truncate leading-tight ${roomDeleted ? 'pt-0' : 'pt-0.5'}`}>
                    {instance.course}
                </div>
                <div className="px-1 text-xs text-gray-400 truncate leading-tight">
                    {instance.room}
                </div>
                <div className="px-1 pb-0.5 text-xs text-gray-400 truncate leading-tight">
                    {instance.groupsFormatted}
                </div>
            </div>
        );
    };

    const renderDayBar = (weekData, day) => {
        const dayInstances = weekData[day.toLowerCase()] || []; // Convert day to lowercase to match stored data

        return (
            <div key={day} className="relative mb-2">
                {/* Day label */}
                <div className="absolute -left-20 top-0 w-16 text-xs text-gray-700 font-semibold flex items-center h-14">
                    {day.substring(0, 3)}
                </div>

                {/* Day time grid */}
                <div
                    className="relative bg-gray-50 border border-gray-200 h-14 rounded-sm hover:bg-gray-100 transition-colors duration-150 overflow-hidden"
                    style={{ width: `${totalGridWidth}px` }}
                >
                    {/* Hour grid lines */}
                    {hourMarkers.map(marker => (
                        <div
                            key={marker.hour}
                            className="absolute top-0 bottom-0 border-l border-gray-300 opacity-60"
                            style={{ left: `${Math.min(marker.position, totalGridWidth - 1)}px` }}
                        />
                    ))}

                    {/* 30-minute grid lines (lighter) */}
                    {hourMarkers.slice(0, -1).map(marker => (
                        <div
                            key={`${marker.hour}-30`}
                            className="absolute top-0 bottom-0 border-l border-gray-200 opacity-40"
                            style={{ left: `${marker.position + (30 * pixelsPerMinute)}px` }}
                        />
                    ))}

                    {/* Course blocks */}
                    {dayInstances.map(instance => renderCourseBlock(instance))}
                </div>
            </div>
        );
    };

    const renderWeek = (weekInfo) => {
        const { year: weekYear, week } = weekInfo;
        const weekKey = `${weekYear}-${week}`;
        const weekData = scheduleByWeekAndDay[weekKey] || {};

        // Get week date range for display
        const weekDateRange = getWeekDateRange(weekYear, week);
        const weekLabel = weekDateRange && weekDateRange.startDate && weekDateRange.endDate
            ? `Week ${week} (${weekDateRange.startDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })} - ${weekDateRange.endDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })})`
            : `Week ${week} - ${weekYear}`;

        // Check if this week has any classes
        const hasClasses = Object.values(weekData).some(dayData => dayData.length > 0);

        // Check if this is the current week
        const isCurrentWeek = weekYear === currentYear && week === currentWeek;

        return (
            <div
                key={weekKey}
                ref={el => currentWeekRefs.current[weekKey] = el}
                className={`mb-6 p-4 bg-white rounded-lg shadow-sm border transition-all duration-200 ${!hasClasses ? 'opacity-60' : ''} ${isCurrentWeek ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                style={{ width: 'fit-content' }}
            >
                <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                    {weekLabel}
                    {isCurrentWeek && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Current Week
                        </span>
                    )}
                    {hasClasses && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {Object.values(weekData).reduce((total, dayData) => total + dayData.length, 0)} classes
                        </span>
                    )}
                </h3>

                <div className="relative pl-20">
                    {/* Time header */}
                    <div className="relative mb-3 h-6" style={{ width: `${totalGridWidth}px` }}>
                        {hourMarkers.map(marker => (
                            <div
                                key={marker.hour}
                                className="absolute text-xs text-gray-500 font-medium"
                                style={{
                                    left: `${marker.position}px`,
                                    transform: 'translateX(-50%)'
                                }}
                            >
                                {marker.label}
                            </div>
                        ))}
                    </div>

                    {/* Day bars */}
                    <div className="space-y-1">
                        {daysOfWeek.map(day => renderDayBar(weekData, day))}
                    </div>
                </div>
            </div>
        );
    };

    if (!teacher) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-center">
                    <p className="text-gray-500 text-lg mb-2">Please select a teacher to view their schedule.</p>
                    <p className="text-gray-400 text-sm">Choose a teacher from the dropdown above.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-gray-50">
            <div className="p-6">
                <div className="mb-6 bg-white rounded-lg shadow-sm border p-4">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Teacher Schedule: {typeof teacher === 'object' && teacher !== null ? teacher.name : teacher}
                            </h2>
                            <p className="text-gray-600 text-sm">
                                Showing all scheduled classes for {typeof teacher === 'object' && teacher !== null ? teacher.name : teacher} starting from week {startWeek} of {year}
                                <span className="text-gray-500"> (Academic year {year}-{year + 1})</span>
                            </p>
                        </div>
                        <button
                            onClick={scrollToCurrentWeek}
                            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-150"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Jump to Current Week
                        </button>
                    </div>
                </div>

                <div className="space-y-6">
                    {weeks.map(weekInfo => renderWeek(weekInfo))}
                </div>
            </div>
        </div>
    );
};

export default TeacherView;
